import React from 'react';
import { MatchupTopPlayer } from '@/api/schemas';
import { formatPlayerDisplayName } from '@/utils/formatting';
import InjuryBadge from '@/components/common/InjuryBadge';

// ============================================================================
// Per-team top 5 projected players. One table per team; BlogPostPage renders
// two of these per matchup (home then away). Visual parity with Top10Table:
// yellow-bordered card, dark navy bg, sticky rank+player columns on mobile,
// yellow-accent PROJ column in the visual role Top10Table gives to PTS.
// ============================================================================

interface MatchupPlayersTableProps {
  heading: string;                              // e.g. "TOP 5 ARSENAL"
  players: MatchupTopPlayer[];
  onPlayerClick?: (player: MatchupTopPlayer) => void;
}

const formatSalary = (n: number | null | undefined): string =>
  n != null ? `$${n.toLocaleString('en-US')}` : '—';

const formatFloat = (n: number | null | undefined, digits: number = 1): string =>
  n != null ? n.toFixed(digits) : '—';

const formatInt = (n: number | null | undefined): string =>
  n != null ? String(Math.round(n)) : '—';

const RankBadge: React.FC<{ rank: number }> = ({ rank }) => (
  <span
    className={[
      'inline-flex items-center justify-center rounded-md',
      'bg-accent-yellow text-primary',
      'font-heading font-bold tracking-wide',
      'shadow-[0_2px_8px_rgba(255,196,44,0.25)]',
      'min-w-[28px] sm:min-w-[40px] h-[24px] sm:h-[30px] px-2 sm:px-3 text-caption sm:text-sm',
    ].join(' ')}
  >
    {rank}
  </span>
);

const PositionPill: React.FC<{ position: string | null | undefined }> = ({ position }) => {
  if (!position) return null;
  return (
    <span className="ml-1.5 sm:ml-2 inline-block align-middle px-1.5 sm:px-2 py-0 sm:py-0.5 rounded bg-gray-200/10 text-gray-300 font-mono font-bold text-micro sm:text-badge tracking-wider whitespace-nowrap">
      <span className="text-accent-yellow">{position}</span>
    </span>
  );
};

const MatchupPlayersTable: React.FC<MatchupPlayersTableProps> = ({
  heading,
  players,
  onPlayerClick,
}) => {
  return (
    <div className="w-full mb-4">
      <div className="relative overflow-hidden rounded-lg border border-accent-yellow/60 bg-surface-card shadow-[0_10px_40px_rgba(0,0,0,0.35)]">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            {/* ===== Header ===== */}
            <thead>
              <tr className="bg-accent-yellow/10 border-b border-accent-yellow/60">
                <th
                  colSpan={2}
                  className="text-left pl-2 sm:pl-6 py-3 sm:py-4 font-heading font-bold text-sm sm:text-lg tracking-[1.5px] sm:tracking-[2px] uppercase text-white whitespace-nowrap sticky left-0 bg-surface-raised z-10"
                >
                  {heading}
                </th>
                <th className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-accent-yellow/10 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                  Proj
                </th>
                <th className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-primary-dark/60 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                  Total FP
                </th>
                <th className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-primary-dark/60 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                  FPPG
                </th>
                <th className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-primary-dark/60 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                  Sal
                </th>
              </tr>
            </thead>

            {/* ===== Body ===== */}
            <tbody>
              {players.length === 0 && (
                <tr>
                  <td colSpan={99} className="py-8 text-center text-gray-500 italic text-sm">
                    No projected players for this team yet.
                  </td>
                </tr>
              )}
              {players.map((player, idx) => {
                const isEven = idx % 2 === 1;
                const isLast = idx === players.length - 1;
                const isClickable = !!onPlayerClick;
                return (
                  <tr
                    key={player.player_id}
                    className={[
                      isEven ? 'bg-primary-dark/30' : '',
                      isLast ? '' : 'border-b border-gray-700/40',
                      isClickable ? 'cursor-pointer hover:bg-primary/50 transition-colors duration-150 ease-in-out' : '',
                    ].join(' ')}
                    onClick={isClickable ? () => onPlayerClick!(player) : undefined}
                    role={isClickable ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    onKeyDown={
                      isClickable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onPlayerClick!(player);
                            }
                          }
                        : undefined
                    }
                  >
                    {/* Rank */}
                    <td className="pl-2 pr-1 sm:pl-6 sm:pr-2 py-3 sm:py-4 text-left whitespace-nowrap sticky left-0 bg-surface-card z-[5]">
                      <RankBadge rank={idx + 1} />
                    </td>
                    {/* Player + position pill */}
                    <td className="py-3 sm:py-4 pr-2 text-left whitespace-nowrap sticky left-[50px] sm:left-[88px] bg-surface-card z-[5]">
                      <span
                        className={[
                          'font-body font-bold text-[13px] sm:text-base text-white tracking-tight',
                          isClickable ? 'hover:text-accent-yellow hover:underline underline-offset-2' : '',
                        ].join(' ')}
                      >
                        {formatPlayerDisplayName({
                          id: player.player_id,
                          name: player.name,
                          nickname: player.nickname,
                        })}
                      </span>
                      <PositionPill position={player.position} />
                      {player.injury_status && (
                        <InjuryBadge
                          status={player.injury_status}
                          details={player.injury_details}
                          size={13}
                          className="ml-1.5 align-middle"
                        />
                      )}
                    </td>
                    {/* PROJ (highlighted, money column) */}
                    <td className="px-2 sm:px-3 py-3 sm:py-4 text-center whitespace-nowrap font-heading font-bold text-lg sm:text-xl tracking-wider text-accent-yellow bg-accent-yellow/5">
                      {formatFloat(player.projected_points)}
                    </td>
                    {/* TP (season total, integer) */}
                    <td className="px-2 sm:px-3 py-3 sm:py-4 text-center whitespace-nowrap font-mono font-medium text-sm text-gray-200">
                      {formatInt(player.total_points)}
                    </td>
                    {/* FPPG */}
                    <td className="px-2 sm:px-3 py-3 sm:py-4 text-center whitespace-nowrap font-mono font-medium text-sm text-gray-200">
                      {formatFloat(player.fppg)}
                    </td>
                    {/* Salary */}
                    <td className="px-2 sm:px-3 py-3 sm:py-4 text-center whitespace-nowrap font-mono font-semibold text-sm text-white">
                      {formatSalary(player.salary)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MatchupPlayersTable;
